import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  getMembers,
  addMember,
  updateMember,
  removeMember,
  getInvitedMembers,
  sendInvitation,
  revokeInvitation,
  resendInvitation,
} from "../api/user";
import { rolesAPI } from "../api/roles";
import {
  PencilSquareIcon,
  XMarkIcon,
  ClipboardIcon,
  ArrowPathIcon,
  TrashIcon,
  PaperAirplaneIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import PermissionGuard from "../components/common/PermissionGuard";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import TableSkeleton from "../components/TableSkeleton";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import {
  Tabs,
  AnimatedModal,
  DataGrid,
  Dropdown,
  Checkbox,
  Input,
} from "../components/ui";

const Members = () => {
  const { activeCompany, organization } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const [activeTab, setActiveTab] = useState("members");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [members, setMembers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);

  // Invite states
  const [invitedMembers, setInvitedMembers] = useState([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [inviteStatusFilter, setInviteStatusFilter] = useState("PENDING");
  const [isInviteSidebarOpen, setIsInviteSidebarOpen] = useState(false);
  const [isInviteSidebarVisible, setIsInviteSidebarVisible] = useState(false);

  // Resend invite states
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);

  // Delete member states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Revoke invitation states
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [inviteToRevoke, setInviteToRevoke] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const formatRole = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.name : roleId;
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    role: Yup.string().required("Role is required"),
    isActive: Yup.boolean(),
  });

  const inviteValidationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    role: Yup.string().required("Role is required"),
  });

  const resendValidationSchema = Yup.object({
    role: Yup.string().required("Role is required"),
    message: Yup.string().optional(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      role: "",
      isActive: true,
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (editingMember) {
          await updateMember(editingMember._id, values);
          fetchMembers();
          toast.success("Member updated successfully");
        } else {
          await addMember(values);
          fetchMembers();
          toast.success("Member added successfully");
        }
        resetForm();
        closeSidebar();
        setEditingMember(null);
      } catch (error) {
        toast.error(error.message || "Failed to save member");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const inviteFormik = useFormik({
    initialValues: {
      email: "",
      role: "",
    },
    validationSchema: inviteValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        await sendInvitation(values);
        fetchInvitedMembers();
        toast.success("Invitation sent successfully");
        resetForm();
        closeInviteSidebar();
      } catch (error) {
        toast.error(error.message || "Failed to send invitation");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const resendFormik = useFormik({
    initialValues: {
      role: "",
      message: "",
    },
    validationSchema: resendValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        await resendInvitation(resendingInvite._id, values);
        fetchInvitedMembers();
        toast.success("Invitation resent successfully");
        resetForm();
        closeResendModal();
      } catch (error) {
        toast.error(error.message || "Failed to resend invitation");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getRoleColor = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return "bg-gray-100 text-gray-800";

    // You can customize colors based on role names or add a color property to roles
    switch (role.name.toLowerCase()) {
      case "admin":
      case "owner":
        return "bg-purple-100 text-purple-800";
      case "manager":
        return "bg-blue-100 text-blue-800";
      case "hr":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Handle sidebar animation
  useEffect(() => {
    if (isSidebarOpen) {
      // Wait for next tick to trigger transition
      setTimeout(() => setSidebarVisible(true), 10);
    } else {
      setSidebarVisible(false);
    }
  }, [isSidebarOpen]);

  // Handle invite sidebar animation
  useEffect(() => {
    if (isInviteSidebarOpen) {
      // Wait for next tick to trigger transition
      setTimeout(() => setIsInviteSidebarVisible(true), 10);
    } else {
      setIsInviteSidebarVisible(false);
    }
  }, [isInviteSidebarOpen]);

  const closeSidebar = () => {
    setSidebarVisible(false);
    setTimeout(() => {
      setIsSidebarOpen(false);
      setEditingMember(null);
      formik.resetForm();
    }, 300);
  };

  const closeInviteSidebar = () => {
    setIsInviteSidebarVisible(false);
    setTimeout(() => {
      setIsInviteSidebarOpen(false);
      inviteFormik.resetForm();
    }, 300);
  };

  const closeResendModal = () => {
    setIsResendModalOpen(false);
    setResendingInvite(null);
    resendFormik.resetForm();
  };

  const getInviteStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "ACCEPTED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "REVOKED":
        return "bg-gray-100 text-gray-800";
      case "EXPIRED":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const openRevokeModal = (invite) => {
    setInviteToRevoke(invite);
    setIsRevokeModalOpen(true);
  };

  const closeRevokeModal = () => {
    setIsRevokeModalOpen(false);
    setInviteToRevoke(null);
  };

  const handleRevokeConfirm = async () => {
    if (!inviteToRevoke) return;
    try {
      setIsRevoking(true);
      await revokeInvitation(inviteToRevoke._id);
      fetchInvitedMembers();
      toast.success("Invitation revoked successfully");
      closeRevokeModal();
    } catch (error) {
      toast.error(error.message || "Failed to revoke invitation");
    } finally {
      setIsRevoking(false);
    }
  };

  const handleResendInvitation = (invite) => {
    setResendingInvite(invite);
    resendFormik.setValues({
      role: invite.role?._id || "",
      message: invite.message || "",
    });
    setIsResendModalOpen(true);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    formik.setValues({
      name: member.name,
      email: member.email,
      role: member?.role?._id || "",
      isActive: member.isActive,
    });
    setIsSidebarOpen(true);
  };

  const openDeleteModal = (member) => {
    setMemberToDelete(member);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;
    try {
      setIsRemoving(true);
      await removeMember(memberToDelete._id);
      fetchMembers();
      toast.success("Member removed successfully");
      closeDeleteModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to remove member",
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const fetchMembers = async () => {
    try {
      setIsLoadingMembers(true);
      const response = await getMembers();
      setMembers(response?.data || []);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error(error.message || "Failed to fetch members");
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const response = await rolesAPI.getRoles();
      setRoles(response?.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error(error.response?.data?.message || "Failed to fetch roles");
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchInvitedMembers = useCallback(async () => {
    try {
      setIsLoadingInvites(true);
      const response = await getInvitedMembers(inviteStatusFilter || null);
      setInvitedMembers(response?.data?.invites || []);
    } catch (error) {
      console.error("Error fetching invited members:", error);
      toast.error(error.message || "Failed to fetch invited members");
    } finally {
      setIsLoadingInvites(false);
    }
  }, [inviteStatusFilter]);

  useEffect(() => {
    fetchMembers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (activeTab === "invites") {
      fetchInvitedMembers();
    }
  }, [activeTab, inviteStatusFilter, fetchInvitedMembers]);

  // Set page header (title + description)
  useEffect(() => {
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Members" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          Members
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl flex items-center gap-2 mt-1">
          Manage your members and view details
          {activeCompany && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              {activeCompany.name}
            </span>
          )}
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [activeCompany, setHeaderContent]);

  const handleCopyInvite = (inviteId) => {
    navigator.clipboard.writeText(
      `${window.location.origin}/invite/${inviteId}`,
    );
    toast.success("Invite link copied to clipboard");
  };

  const memberTabs = [
    { value: "members", label: "Members" },
    ...(organization?.planMeta?.USER_CREATION >= 1 ||
    organization?.planMeta?.USER_CREATION === -1
      ? [{ value: "invites", label: "Invites" }]
      : []),
  ];

  return (
    <PagePermissionGuard module="members" action="read" pageName="Members">
      <div>
        <div className="">
          <Tabs tabs={memberTabs} value={activeTab} onChange={setActiveTab} />
        </div>
        <div className="bg-white overflow-hidden">
          {/* Members Tab Content */}
          {activeTab === "members" && (
            <>
              {isLoadingMembers ? (
                <TableSkeleton rows={8} cols={5} showHeader={true} />
              ) : (
                <DataGrid
                  columns={[
                    {
                      key: "name",
                      label: "Name",
                      sortable: false,
                      render: (_, row) => (
                        <span className="text-sm font-medium text-gray-900">
                          {row.name}
                        </span>
                      ),
                    },
                    {
                      key: "email",
                      label: "Email",
                      sortable: false,
                      render: (_, row) => (
                        <span className="text-sm text-gray-500">
                          {row.email}
                        </span>
                      ),
                    },
                    {
                      key: "role",
                      label: "Role",
                      sortable: false,
                      render: (_, row) => (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                            row.role,
                          )}`}
                        >
                          {formatRole(row?.role?.name || "")}
                        </span>
                      ),
                    },
                    {
                      key: "status",
                      label: "Status",
                      sortable: false,
                      render: (_, row) => (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            row.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {row.isActive ? "Active" : "Inactive"}
                        </span>
                      ),
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      sortable: false,
                      render: (_, row) =>
                        row.companyRole !== "COMPANY_OWNER" &&
                        row.companyRole !== "ORG_ADMIN" &&
                        row.role?.name !== "COMPANY_OWNER" &&
                        row.role?.name !== "ORG_ADMIN" ? (
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PermissionGuard module="members" action="update">
                              <button
                                onClick={() => handleEdit(row)}
                                className="text-gray-600 hover:text-primary transition-colors duration-200"
                                title="Edit member"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(row)}
                                className="text-red-600 hover:text-red-800 transition-colors duration-200"
                                title="Remove member"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </PermissionGuard>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        ),
                    },
                  ]}
                  data={members.map((m) => ({ ...m, id: m.id || m._id }))}
                  headerVariant="white"
                  emptyMessage="No members found"
                  className="!rounded-none !shadow-none"
                  minHeight={320}
                />
              )}
            </>
          )}

          {/* Invites Tab Content */}
          {activeTab === "invites" && (
            <div>
              {/* Status Filter */}
              <div className="px-4 py-5 flex justify-between items-center gap-4">
                <div className="min-w-[160px]">
                  <Dropdown
                    value={inviteStatusFilter}
                    onChange={(val) => setInviteStatusFilter(val)}
                    options={[
                      { value: "PENDING", label: "Pending" },
                      { value: "ACCEPTED", label: "Accepted" },
                      { value: "REJECTED", label: "Rejected" },
                      { value: "REVOKED", label: "Revoked" },
                      { value: "EXPIRED", label: "Expired" },
                    ]}
                    placeholder="Status"
                    size="sm"
                    className="!mb-0"
                  />
                </div>

                <PermissionGuard module="members" action="write">
                  <button
                    onClick={() => {
                      inviteFormik.resetForm();
                      setIsInviteSidebarOpen(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors duration-200 text-sm font-medium"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                    Send Invitation
                  </button>
                </PermissionGuard>
              </div>

              {isLoadingInvites ? (
                <div className="overflow-hidden">
                  <TableSkeleton rows={6} cols={7} showHeader={true} />
                </div>
              ) : (
                <DataGrid
                  columns={[
                    {
                      key: "copy",
                      label: "",
                      sortable: false,
                      render: (_, row) => (
                        <ClipboardIcon
                          className="h-5 w-5 text-gray-500 cursor-pointer hover:text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyInvite(row.inviteToken);
                          }}
                        />
                      ),
                    },
                    {
                      key: "email",
                      label: "Email",
                      sortable: false,
                      render: (_, row) => (
                        <span className="text-sm font-medium text-gray-900">
                          {row.email}
                        </span>
                      ),
                    },
                    {
                      key: "role",
                      label: "Role",
                      sortable: false,
                      render: (_, row) => (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                            row.role?._id,
                          )}`}
                        >
                          {row.role?.name || "N/A"}
                        </span>
                      ),
                    },
                    {
                      key: "status",
                      label: "Status",
                      sortable: false,
                      render: (_, row) => (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInviteStatusColor(
                            row.status,
                          )}`}
                        >
                          {row.status}
                        </span>
                      ),
                    },
                    {
                      key: "invitedBy",
                      label: "Invited By",
                      sortable: false,
                      render: (_, row) => (
                        <span className="text-sm text-gray-500">
                          {row.invitedBy?.name || row.invitedBy?.email || "N/A"}
                        </span>
                      ),
                    },
                    {
                      key: "invitedAt",
                      label: "Invited At",
                      sortable: false,
                      render: (_, row) => (
                        <span className="text-sm text-gray-500">
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleDateString()
                            : "N/A"}
                        </span>
                      ),
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      sortable: false,
                      render: (_, row) =>
                        row.status === "PENDING" ? (
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PermissionGuard module="members" action="write">
                              <button
                                onClick={() => handleResendInvitation(row)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                title="Resend invitation"
                              >
                                <ArrowPathIcon className="h-5 w-5" />
                              </button>
                            </PermissionGuard>
                            <PermissionGuard module="members" action="update">
                              <button
                                onClick={() => openRevokeModal(row)}
                                className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                title="Revoke invitation"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </PermissionGuard>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        ),
                    },
                  ]}
                  data={invitedMembers.map((i) => ({ ...i, id: i._id }))}
                  headerVariant="white"
                  emptyMessage="No invitations found"
                  className="!rounded-none !shadow-none"
                  minHeight={320}
                />
              )}
            </div>
          )}

          {/* Add/Edit Member Sidebar */}
          {isSidebarOpen && (
            <div className="fixed inset-0 z-50">
              {/* Overlay */}
              <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
                  sidebarVisible ? "opacity-100" : "opacity-0"
                }`}
                onClick={closeSidebar}
              />
              {/* Sidebar Panel */}
              <div className="fixed inset-y-0 right-0 flex max-w-full">
                <div
                  className={`w-screen max-w-md transform transition-transform duration-300 ease-in-out bg-white shadow-xl h-full flex flex-col ${
                    sidebarVisible ? "translate-x-0" : "translate-x-full"
                  }`}
                >
                  <div className="px-6 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="font-heading text-lg font-semibold text-gray-900">
                        {editingMember ? "Edit Member" : "Add New Member"}
                      </h2>
                      <button
                        type="button"
                        onClick={closeSidebar}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto sidebar-scroll">
                    <div className="px-6 py-6">
                      <form
                        onSubmit={formik.handleSubmit}
                        className="space-y-5"
                      >
                        <div>
                          <Input
                            id="name"
                            name="name"
                            label="Name"
                            type="text"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter member name"
                            error={
                              formik.errors.name && formik.touched.name
                                ? formik.errors.name
                                : undefined
                            }
                            disabled={false}
                            size="sm"
                          />
                        </div>
                        <div>
                          <Input
                            id="email"
                            name="email"
                            label="Email"
                            type="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter member email"
                            error={
                              formik.errors.email && formik.touched.email
                                ? formik.errors.email
                                : undefined
                            }
                            disabled={editingMember}
                            size="sm"
                          />
                        </div>
                        <div>
                          <Dropdown
                            label="Role"
                            value={formik.values.role}
                            onChange={(val) =>
                              formik.setFieldValue("role", val)
                            }
                            onBlur={() => formik.setFieldTouched("role")}
                            options={roles.map((r) => ({
                              value: r._id,
                              label: r.name,
                            }))}
                            placeholder={
                              isLoadingRoles
                                ? "Loading roles..."
                                : "Select role"
                            }
                            disabled={isLoadingRoles}
                            error={
                              !!(formik.errors.role && formik.touched.role)
                            }
                            size="sm"
                          />
                          {formik.errors.role && formik.touched.role && (
                            <p className="mt-1.5 text-sm text-red-600">
                              {formik.errors.role}
                            </p>
                          )}
                        </div>
                        <div>
                          <Checkbox
                            name="isActive"
                            checked={formik.values.isActive}
                            onChange={formik.handleChange}
                            label="Active"
                          />
                        </div>
                        <div className="pt-2">
                          <button
                            type="submit"
                            disabled={formik.isSubmitting}
                            className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {formik.isSubmitting ? (
                              <>
                                <svg
                                  className="animate-spin h-5 w-5"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                {editingMember ? "Updating..." : "Adding..."}
                              </>
                            ) : editingMember ? (
                              <>
                                <PencilSquareIcon className="h-5 w-5" />
                                Update Member
                              </>
                            ) : (
                              <>
                                <UserPlusIcon className="h-5 w-5" />
                                Add Member
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Send Invitation Sidebar */}
          {isInviteSidebarOpen && (
            <div className="fixed inset-0 z-50">
              {/* Overlay */}
              <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
                  isInviteSidebarVisible ? "opacity-100" : "opacity-0"
                }`}
                onClick={closeInviteSidebar}
              />
              {/* Sidebar Panel */}
              <div className="fixed inset-y-0 right-0 flex max-w-full">
                <div
                  className={`w-screen max-w-md transform transition-transform duration-300 ease-in-out bg-white shadow-xl h-full flex flex-col ${
                    isInviteSidebarVisible
                      ? "translate-x-0"
                      : "translate-x-full"
                  }`}
                >
                  <div className="px-6 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="font-heading text-lg font-semibold text-gray-900">
                        Send Invitation
                      </h2>
                      <button
                        type="button"
                        onClick={closeInviteSidebar}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto sidebar-scroll">
                    <div className="px-6 py-6">
                      <form
                        onSubmit={inviteFormik.handleSubmit}
                        className="space-y-5"
                      >
                        <div>
                          <Input
                            id="invite-email"
                            name="email"
                            label="Email"
                            type="email"
                            value={inviteFormik.values.email}
                            onChange={inviteFormik.handleChange}
                            onBlur={inviteFormik.handleBlur}
                            placeholder="Enter email address"
                            error={
                              inviteFormik.errors.email &&
                              inviteFormik.touched.email
                                ? inviteFormik.errors.email
                                : undefined
                            }
                            size="sm"
                          />
                        </div>
                        <div>
                          <Dropdown
                            label="Role"
                            value={inviteFormik.values.role}
                            onChange={(val) =>
                              inviteFormik.setFieldValue("role", val)
                            }
                            onBlur={() => inviteFormik.setFieldTouched("role")}
                            options={roles.map((r) => ({
                              value: r._id,
                              label: r.name,
                            }))}
                            placeholder={
                              isLoadingRoles
                                ? "Loading roles..."
                                : "Select role"
                            }
                            disabled={isLoadingRoles}
                            error={
                              !!(
                                inviteFormik.errors.role &&
                                inviteFormik.touched.role
                              )
                            }
                            size="sm"
                          />
                          {inviteFormik.errors.role &&
                            inviteFormik.touched.role && (
                              <p className="mt-1.5 text-sm text-red-600">
                                {inviteFormik.errors.role}
                              </p>
                            )}
                        </div>
                        <div className="pt-2">
                          <button
                            type="submit"
                            disabled={inviteFormik.isSubmitting}
                            className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {inviteFormik.isSubmitting ? (
                              <>
                                <svg
                                  className="animate-spin h-5 w-5"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Sending...
                              </>
                            ) : (
                              <>
                                <PaperAirplaneIcon className="h-5 w-5" />
                                Send Invitation
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resend Invitation Modal */}
          <AnimatedModal
            isOpen={!!(isResendModalOpen && resendingInvite)}
            onClose={closeResendModal}
            closeOnBackdrop
            contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-md sm:w-full max-h-[90vh] overflow-y-auto flex flex-col"
          >
            <div className="bg-white max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Resend Invitation
                  </h3>
                  <button
                    type="button"
                    onClick={closeResendModal}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <span className="sr-only">Close modal</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Resending invitation to{" "}
                  <strong>{resendingInvite?.email}</strong>
                </p>
              </div>
              <div className="px-6 py-4">
                <form
                  onSubmit={resendFormik.handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <Dropdown
                      label="Role"
                      value={resendFormik.values.role}
                      onChange={(val) =>
                        resendFormik.setFieldValue("role", val)
                      }
                      onBlur={() => resendFormik.setFieldTouched("role")}
                      options={roles.map((r) => ({
                        value: r._id,
                        label: r.name,
                      }))}
                      placeholder={
                        isLoadingRoles ? "Loading roles..." : "Select role"
                      }
                      disabled={isLoadingRoles}
                      error={
                        !!(
                          resendFormik.errors.role && resendFormik.touched.role
                        )
                      }
                      size="sm"
                    />
                    {resendFormik.errors.role && resendFormik.touched.role && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {resendFormik.errors.role}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="resend-message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Message (Optional)
                    </label>
                    <textarea
                      id="resend-message"
                      name="message"
                      rows={3}
                      value={resendFormik.values.message}
                      onChange={resendFormik.handleChange}
                      onBlur={resendFormik.handleBlur}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                        resendFormik.errors.message &&
                        resendFormik.touched.message
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-primary"
                      }`}
                      placeholder="Enter a custom message for the invitation"
                    />
                    {resendFormik.errors.message &&
                      resendFormik.touched.message && (
                        <p className="mt-2 text-sm text-red-600">
                          {resendFormik.errors.message}
                        </p>
                      )}
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeResendModal}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={resendFormik.isSubmitting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendFormik.isSubmitting ? (
                        <div className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Resending...
                        </div>
                      ) : (
                        "Resend Invitation"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </AnimatedModal>

          {/* Revoke Invitation Confirmation Modal */}
          <AnimatedModal
            isOpen={!!(isRevokeModalOpen && inviteToRevoke)}
            onClose={closeRevokeModal}
            closeOnBackdrop
            contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-md sm:w-full"
          >
            <div className="bg-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Revoke Invitation
                </h3>
                <button
                  type="button"
                  onClick={closeRevokeModal}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary p-1"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to revoke the invitation sent to{" "}
                <strong>{inviteToRevoke?.email}</strong>? They will no longer be
                able to accept this invitation.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRevokeModal}
                disabled={isRevoking}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevokeConfirm}
                disabled={isRevoking}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRevoking ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Revoking...
                  </span>
                ) : (
                  "Revoke"
                )}
              </button>
            </div>
          </AnimatedModal>

          {/* Remove Member Confirmation Modal */}
          <AnimatedModal
            isOpen={!!(isDeleteModalOpen && memberToDelete)}
            onClose={closeDeleteModal}
            closeOnBackdrop
            contentClassName="bg-white shadow-xl overflow-hidden sm:max-w-md sm:w-full"
          >
            <div className="bg-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Remove Member
                </h3>
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary p-1"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to remove{" "}
                <strong>{memberToDelete?.name}</strong> from this company? They
                will lose access immediately.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={isRemoving}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemoveMember}
                disabled={isRemoving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRemoving ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Removing...
                  </span>
                ) : (
                  "Remove"
                )}
              </button>
            </div>
          </AnimatedModal>
        </div>
      </div>
    </PagePermissionGuard>
  );
};

export default Members;
